import React from 'react'

import { useQuery } from '@tanstack/react-query'
import { twMerge } from 'tailwind-merge'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

interface ChangelogMediaUrlResponse {
  url: string
}

interface ChangelogVideoProps {
  mediaId: string
  className?: string
  // CSS aspect-ratio for the player frame (e.g. "16 / 9", "1424 / 1080").
  // Match the source video to avoid letterbox/pillarbox bars. Defaults to 16:9.
  aspectRatio?: string
  // Overrides the frame background (defaults to black). Use a color matching
  // the recording (e.g. "bg-white") so the rounded-corner clip seam blends in.
  frameClassName?: string
  // Inset overlay drawn on top of the video edges (e.g. "ring-2 ring-inset
  // ring-white") to mask thin artifacts baked into the source recording.
  edgeMaskClassName?: string
}

const MEDIA_URL_CACHE_TIME_MS = 1000 * 60 * 30

const fetchChangelogMediaUrl = async (
  mediaId: string,
  accessToken: string | null | undefined,
): Promise<string> => {
  if (!accessToken) {
    throw new Error('No access token provided.')
  }

  const res = await fetch(`${API_URL}/api/v1/changelog/media/${mediaId}/url`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new Error(res.statusText)
  }

  const data = (await res.json()) as ChangelogMediaUrlResponse
  return data.url
}

export const ChangelogVideo: React.FC<ChangelogVideoProps> = ({
  mediaId,
  className,
  aspectRatio = '16 / 9',
  frameClassName,
  edgeMaskClassName,
}) => {
  const { accessToken, loading: authIsLoading } = useAuthInfo()
  const { data: url, isLoading } = useQuery({
    queryKey: ['changelog', 'media', mediaId, accessToken ?? ''],
    queryFn: () => fetchChangelogMediaUrl(mediaId, accessToken),
    enabled: !!accessToken,
    staleTime: MEDIA_URL_CACHE_TIME_MS,
    gcTime: MEDIA_URL_CACHE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  return (
    <div
      className={twMerge(
        'changelog-video-shell relative my-6 w-full rounded-xl border border-primary-200 dark:border-primary-700/50 bg-black overflow-hidden',
        frameClassName,
      )}
      style={{ aspectRatio }}
    >
      {url ? (
        <>
          <video
            controls
            preload="metadata"
            playsInline
            src={url}
            className={twMerge(
              'block size-full object-contain bg-black',
              className,
            )}
          />
          {edgeMaskClassName ? (
            <div
              aria-hidden
              className={twMerge(
                'pointer-events-none absolute inset-0 rounded-[inherit]',
                edgeMaskClassName,
              )}
            />
          ) : null}
        </>
      ) : (
        <div className="flex size-full items-center justify-center px-4 text-sm text-primary-200">
          {authIsLoading || isLoading
            ? 'Loading video...'
            : 'Unable to load video.'}
        </div>
      )}
    </div>
  )
}

export default ChangelogVideo
